import Nexus from 'nexusui';
import { FULL_RANGE_END, FULL_RANGE_START, LETTERS, MAX_ABS, MIN_ABS, absIndex, clamp, fromAbsIndex, toneName } from '../config/music';
import { INSTRUMENT_ORDER, PRESETS, PRESET_BUTTONS } from '../config/presets';
import type { PresetId } from '../config/presets';
import { SYNTH_TYPES, SYNTH_TYPE_IDS, WAVEFORMS, defaultExtraValues } from '../config/synth-types';
import type { Waveform } from '../config/synth-types';
import { CUSTOM_METER_ID, METERS, accentsFor } from '../config/meters';
import type { AccentLevel } from '../config/meters';
import type { Settings, SoundProfile } from '../state/settings';
import { DEFAULT_SETTINGS, clearPersisted } from '../state/settings';
import * as store from '../state/store';
import { registerCurveDb } from '../audio/engine';
import { onSamplerStatus } from '../audio/sampler';
import type { SamplerStatus } from '../audio/sampler';
import { setEchoWet, setFilterFrequency, setReverbWet, setVolume } from '../audio/graph';
import * as metronome from '../audio/metronome';
import { button, el, fillSelect, input, option, select, setStatus, show } from './dom';
import { instrumentLabel, meterLabel, presetLabel, synthLabel, t, waveformName } from '../i18n';
import { createBinder } from './bind';
import { createRangeSlider } from './range-slider';

export interface ControlHooks {
  requestKeyboardRender: () => void;
  rebuildSource: () => void;
  applySound: () => void;
  renderExtraParams: () => void;
  panic: () => void;
  onReset: () => void;
}

export function initControls(hooks: ControlHooks): { syncFromState: () => void } {
  const dom = {
    showLetters: input('showLetters'),
    showSolfege: input('showSolfege'),
    showKeys: input('showKeys'),
    showOctave: input('showOctave'),
    startNote: select('startNote'),
    startOctave: select('startOctave'),
    octaveCount: select('octaveCount'),
    displayMode: select('displayMode'),

    engine: select('engine'),
    synthType: select('synthType'),
    synthTypeField: el('synthTypeField'),
    sampleInstrument: select('sampleInstrument'),
    sampleInstrumentField: el('sampleInstrumentField'),
    sampleDensity: select('sampleDensity'),
    sampleDensityField: el('sampleDensityField'),
    samplerStatus: el('samplerStatus'),
    engineNotice: el('engineNotice'),

    presetField: el('presetField'),
    presetButtons: el('presetButtons'),
    waveformField: el('waveformField'),
    waveformButtons: el('waveformButtons'),
    attackField: el('attackField'),
    attackValue: el('attackValue'),
    releaseField: el('releaseField'),
    releaseValue: el('releaseValue'),
    sustain: input('sustain'),
    extraParams: el('synthExtraParams'),

    rangeSlider: el('rangeSlider'),
    rangeMinLabel: el('rangeMinLabel'),
    rangeMaxLabel: el('rangeMaxLabel'),
    rangeCountLabel: el('rangeCountLabel'),

    bassCurve: input('bassCurve'),
    trebleCurve: input('trebleCurve'),
    bassCurveValue: el('bassCurveValue'),
    trebleCurveValue: el('trebleCurveValue'),
    curveOnSamples: input('curveOnSamples'),
    curvePreview: el('curvePreview'),

    resetInstrumentBtn: button('resetInstrumentBtn'),

    bpm: input('bpm'),
    bpmDown: button('bpmDown'),
    bpmUp: button('bpmUp'),
    tapTempo: button('tapTempo'),
    metronomeBtn: button('metronomeBtn'),
    metronomePulse: el('metronomePulse'),
    meter: select('meter'),
    meterCustom: el('meterCustom'),
    meterMinus: button('meterMinus'),
    meterPlus: button('meterPlus'),
    meterPulses: el('meterPulses'),

    volumeValue: el('volumeValue'),
    filterValue: el('filterValue'),
    echoValue: el('echoValue'),
    reverbValue: el('reverbValue'),
  };

  // ---- static select contents --------------------------------------------
  fillSelect(dom.startNote, LETTERS.map((n, i) => [String(i), n] as [string, string]));
  fillSelect(dom.startOctave, Array.from({ length: 9 }, (_, o) => [String(o), String(o)] as [string, string]));
  // The full 88-key range is the default and the first option. Whole-octave
  // counts follow. "partial" is only ever selected programmatically, when the
  // range is neither the full keyboard nor an exact number of octaves (e.g.
  // after dragging the range slider to an odd width).
  const OCTAVES_FULL = 'full';
  const OCTAVES_PARTIAL = 'partial';
  fillSelect(dom.octaveCount, [
    [OCTAVES_FULL, t('octaveCount.full')],
    ...Array.from({ length: 8 }, (_, i) => [String(i + 1), String(i + 1)] as [string, string]),
  ]);
  // "partial" is only ever set programmatically (a range that is neither the
  // full keyboard nor a whole number of octaves), so it is a disabled,
  // indicator-only option the user cannot pick.
  const partialOption = option(OCTAVES_PARTIAL, t('octaveCount.partial'));
  partialOption.disabled = true;
  dom.octaveCount.appendChild(partialOption);
  fillSelect(dom.synthType, SYNTH_TYPE_IDS.map((id) => [id, synthLabel(id)] as [string, string]));
  fillSelect(dom.sampleInstrument, INSTRUMENT_ORDER.map((id) => [id, instrumentLabel(id)] as [string, string]));
  fillSelect(dom.meter, [
    ...METERS.map((m) => [m.id, meterLabel(m.id)] as [string, string]),
    [CUSTOM_METER_ID, t('meter.custom')],
  ]);

  // The low-pass filter is a brightness / tone control. Human hearing is
  // logarithmic, so a linear Hz sweep is inaudible across its top 80%; the dial
  // runs 0..1 and maps exponentially to Hz, giving an even, audible response
  // over the whole travel. The setting itself is still stored in Hz.
  const F_MIN = 200;
  const F_MAX = 12000;
  const posToHz = (pos: number): number =>
    Math.round((F_MIN * Math.pow(F_MAX / F_MIN, clamp(pos, 0, 1)) ) / 25) * 25;
  const hzToPos = (hz: number): number => Math.log(clamp(hz, F_MIN, F_MAX) / F_MIN) / Math.log(F_MAX / F_MIN);
  const filterWord = (hz: number): string => {
    const p = hzToPos(hz);
    if (p < 0.15) return t('filter.veryDark');
    if (p < 0.38) return t('filter.dark');
    if (p < 0.62) return t('filter.neutral');
    if (p < 0.85) return t('filter.bright');
    return t('filter.veryBright');
  };

  // Attack/Release are times in seconds. A squared response gives the short
  // end (where the useful values live) far more of the knob's travel than a
  // linear sweep would. Stored value stays in seconds.
  const timeCurve = (max: number) => ({
    toStored: (pos: number): number => Math.round(pos * pos * max * 1000) / 1000,
    toDial: (sec: number): number => Math.sqrt(clamp(sec, 0, max) / max),
  });
  const attackCurve = timeCurve(2);
  const releaseCurve = timeCurve(10);
  const secs = (v: number): string => `${v.toFixed(v < 1 ? 2 : 1)} s`;

  const initial = store.getSettings();
  const knobs = {
    volume: new Nexus.Dial('#volumeKnob', { size: [64, 64], min: 0, max: 1, step: 0.01, value: initial.volume }),
    filter: new Nexus.Dial('#filterKnob', { size: [64, 64], min: 0, max: 1, step: 0.01, value: hzToPos(initial.filter) }),
    attack: new Nexus.Dial('#attackKnob', { size: [64, 64], min: 0, max: 1, step: 0.01, value: attackCurve.toDial(initial.attack) }),
    release: new Nexus.Dial('#releaseKnob', { size: [64, 64], min: 0, max: 1, step: 0.01, value: releaseCurve.toDial(initial.release) }),
    echo: new Nexus.Dial('#echoKnob', { size: [64, 64], min: 0, max: 1, step: 0.01, value: initial.echo }),
    reverb: new Nexus.Dial('#reverbKnob', { size: [64, 64], min: 0, max: 1, step: 0.01, value: initial.reverb }),
  };

  const markCustom = (): void => {
    if (store.get('preset') !== 'custom') store.update({ preset: 'custom' });
  };
  const binder = createBinder(markCustom);
  const two = (v: number): string => v.toFixed(2);

  // ---- declarative bindings ----------------------------------------------
  binder.field({ el: dom.showLetters, key: 'showLetters', blurOnChange: true, onChange: hooks.requestKeyboardRender });
  binder.field({ el: dom.showSolfege, key: 'showSolfege', blurOnChange: true, onChange: hooks.requestKeyboardRender });
  binder.field({ el: dom.showKeys, key: 'showKeys', blurOnChange: true, onChange: hooks.requestKeyboardRender });
  binder.field({ el: dom.showOctave, key: 'showOctave', blurOnChange: true, onChange: hooks.requestKeyboardRender });
  binder.field({
    el: dom.displayMode,
    key: 'displayMode',
    blurOnChange: true,
    onChange: hooks.requestKeyboardRender,
  });

  binder.field({
    el: dom.engine,
    key: 'engine',
    blurOnChange: true,
    onChange: () => {
      hooks.panic();
      refreshEngineVisibility();
      paintCurve();
      hooks.rebuildSource();
    },
  });
  binder.field({
    el: dom.synthType,
    key: 'synthType',
    blurOnChange: true,
    marksCustom: true,
    onChange: () => {
      hooks.panic();
      refreshEngineVisibility();
      hooks.renderExtraParams();
      hooks.rebuildSource();
    },
  });
  binder.field({
    el: dom.sampleInstrument,
    key: 'sampleInstrument',
    blurOnChange: true,
    onChange: () => {
      hooks.panic();
      hooks.rebuildSource();
    },
  });
  binder.field({
    el: dom.sampleDensity,
    key: 'sampleDensity',
    kind: 'number',
    event: 'change',
    blurOnChange: true,
    onChange: () => {
      hooks.panic();
      hooks.rebuildSource();
    },
  });

  binder.field({ el: dom.sustain, key: 'sustain' });

  binder.field({ el: dom.bassCurve, key: 'bassCurveDb', onChange: paintCurve });
  binder.field({ el: dom.trebleCurve, key: 'trebleCurveDb', onChange: paintCurve });
  binder.field({ el: dom.curveOnSamples, key: 'registerCurveOnSamples', onChange: paintCurve });

  binder.field({ el: dom.bpm, key: 'bpm', onChange: (v) => metronome.setBpm(Number(v)) });

  binder.knob({
    dial: knobs.volume,
    key: 'volume',
    host: el('volumeKnob'),
    label: t('knob.volume'),
    min: 0,
    max: 1,
    step: 0.01,
    readout: dom.volumeValue,
    format: two,
    onChange: setVolume,
  });
  binder.knob({
    dial: knobs.filter,
    key: 'filter',
    host: el('filterKnob'),
    label: t('knob.filter.aria'),
    min: 0,
    max: 1,
    step: 0.02,
    map: { toStored: posToHz, toDial: hzToPos },
    readout: dom.filterValue,
    format: filterWord,
    valueText: (hz) => `${filterWord(hz)} (${Math.round(hz)} Hz)`,
    marksCustom: true,
    onChange: setFilterFrequency,
  });
  binder.knob({
    dial: knobs.attack,
    key: 'attack',
    host: el('attackKnob'),
    label: t('knob.attack'),
    min: 0,
    max: 1,
    step: 0.02,
    map: attackCurve,
    readout: dom.attackValue,
    format: secs,
    marksCustom: true,
    onChange: hooks.applySound,
  });
  binder.knob({
    dial: knobs.release,
    key: 'release',
    host: el('releaseKnob'),
    label: t('knob.release'),
    min: 0,
    max: 1,
    step: 0.02,
    map: releaseCurve,
    readout: dom.releaseValue,
    format: secs,
    marksCustom: true,
    onChange: hooks.applySound,
  });
  binder.knob({
    dial: knobs.echo,
    key: 'echo',
    host: el('echoKnob'),
    label: t('knob.echo'),
    min: 0,
    max: 1,
    step: 0.01,
    readout: dom.echoValue,
    format: two,
    marksCustom: true,
    onChange: setEchoWet,
  });
  binder.knob({
    dial: knobs.reverb,
    key: 'reverb',
    host: el('reverbKnob'),
    label: t('knob.reverb'),
    min: 0,
    max: 1,
    step: 0.01,
    readout: dom.reverbValue,
    format: two,
    marksCustom: true,
    onChange: setReverbWet,
  });

  // ---- keyboard range: three views of one pair of numbers ------------------
  const slider = createRangeSlider(dom.rangeSlider, {
    min: MIN_ABS,
    max: MAX_ABS,
    step: 1,
    minGap: 1,
    labels: [t('range.low.aria'), t('range.high.aria')],
    valueText: toneName,
    onInput: (low, high) => {
      store.update({ startAbs: low, endAbs: high });
      paintRangeLabels();
      syncStartSelects();
      hooks.requestKeyboardRender();
    },
  });

  function paintRangeLabels(): void {
    const s = store.getSettings();
    dom.rangeMinLabel.textContent = toneName(s.startAbs);
    dom.rangeMaxLabel.textContent = toneName(s.endAbs);
    dom.rangeCountLabel.textContent = t('range.keys', { n: s.endAbs - s.startAbs + 1 });
  }

  function syncStartSelects(): void {
    const s = store.getSettings();
    const { octave, noteIndex } = fromAbsIndex(s.startAbs);
    dom.startNote.value = String(noteIndex);
    dom.startOctave.value = String(octave);
    const span = s.endAbs - s.startAbs;
    const whole = span % 12 === 0 && span / 12 >= 1 && span / 12 <= 8;
    dom.octaveCount.value =
      s.startAbs === FULL_RANGE_START && s.endAbs === FULL_RANGE_END
        ? OCTAVES_FULL
        : whole
          ? String(span / 12)
          : OCTAVES_PARTIAL;
  }

  function setRange(startAbs: number, endAbs: number): void {
    store.update({ startAbs, endAbs });
    slider.setValues(startAbs, endAbs);
    paintRangeLabels();
    syncStartSelects();
    hooks.requestKeyboardRender();
  }

  const applyStartFromSelects = (): void => {
    const s = store.getSettings();
    const span = s.endAbs - s.startAbs;
    const start = clamp(absIndex(Number(dom.startOctave.value), Number(dom.startNote.value)), MIN_ABS, MAX_ABS - 1);
    setRange(start, Math.min(start + span, MAX_ABS));
  };
  for (const sel of [dom.startNote, dom.startOctave]) {
    sel.addEventListener('change', () => {
      applyStartFromSelects();
      sel.blur();
    });
  }

  dom.octaveCount.addEventListener('change', () => {
    dom.octaveCount.blur();
    const value = dom.octaveCount.value;
    if (value === OCTAVES_PARTIAL) {
      syncStartSelects(); // not a real choice; restore what the range actually is
      return;
    }
    if (value === OCTAVES_FULL) {
      setRange(FULL_RANGE_START, FULL_RANGE_END);
      return;
    }
    const s = store.getSettings();
    const semitones = Number(value) * 12;
    // Asking for N octaves near the top of the keyboard would otherwise be
    // clipped to fewer; slide the start down so the request is honoured.
    let start = s.startAbs;
    let end = start + semitones;
    if (end > MAX_ABS) {
      end = MAX_ABS;
      start = Math.max(MIN_ABS, end - semitones);
    }
    setRange(start, end);
  });

  // ---- engine visibility ---------------------------------------------------
  function refreshEngineVisibility(): void {
    const s = store.getSettings();
    const isSampler = s.engine === 'sampler';
    const def = SYNTH_TYPES[s.synthType];
    // Presets are synth patches, meaningless for a recorded instrument.
    show(dom.presetField, !isSampler);
    show(dom.synthTypeField, !isSampler);
    show(dom.sampleInstrumentField, isSampler);
    show(dom.sampleDensityField, isSampler);
    show(dom.waveformField, !isSampler && def.hasOscillator);
    // Tone.Sampler has its own attack/release, so those stay useful for samples.
    const hasEnvelope = isSampler || def.envelope !== false;
    show(dom.attackField, hasEnvelope);
    show(dom.releaseField, hasEnvelope);
    show(dom.extraParams, !isSampler);
    renderSamplerStatus(lastSamplerStatus);
  }

  // ---- icon-strip choosers: preset and waveform ---------------------------
  /** Build a small icon button and append it to a strip. */
  function iconButton(
    strip: HTMLElement,
    value: string,
    title: string,
    glyph: SVGElement | string,
    onClick: () => void,
  ): void {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'icon-btn';
    btn.dataset['value'] = value;
    btn.title = title;
    btn.setAttribute('aria-label', title);
    btn.setAttribute('aria-pressed', 'false');
    if (typeof glyph === 'string') {
      const span = document.createElement('span');
      span.className = 'icon-glyph';
      span.textContent = glyph;
      span.setAttribute('aria-hidden', 'true');
      btn.appendChild(span);
    } else {
      btn.appendChild(glyph);
    }
    btn.addEventListener('click', onClick);
    strip.appendChild(btn);
  }

  function markStripActive(strip: HTMLElement, current: string): void {
    for (const btn of strip.querySelectorAll<HTMLButtonElement>('.icon-btn')) {
      const on = btn.dataset['value'] === current;
      btn.classList.toggle('active', on);
      btn.setAttribute('aria-pressed', String(on));
    }
  }

  // Presets
  for (const item of PRESET_BUTTONS) {
    iconButton(dom.presetButtons, item.id, presetLabel(item.id), item.icon, () => selectPreset(item.id));
  }
  // Waveforms — inline SVG of the wave shape.
  const SVG_NS = 'http://www.w3.org/2000/svg';
  for (const wf of WAVEFORMS) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 22 14');
    svg.setAttribute('class', 'wave-icon');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', wf.path);
    svg.appendChild(path);
    iconButton(dom.waveformButtons, wf.id, waveformName(wf.id), svg, () => selectWaveform(wf.id));
  }

  function selectWaveform(id: Waveform): void {
    if (store.get('waveform') === id) return;
    store.update({ waveform: id });
    markCustom();
    markStripActive(dom.waveformButtons, id);
    hooks.applySound();
  }
  function updateWaveformActive(): void {
    markStripActive(dom.waveformButtons, store.get('waveform'));
  }
  store.on(['waveform'], updateWaveformActive);

  function selectPreset(id: PresetId | 'custom'): void {
    if (id === 'custom') {
      // "custom" is the tweaked state, not a real patch to load; only mark it.
      store.update({ preset: 'custom' });
      updatePresetActive();
      return;
    }
    const preset = PRESETS[id];
    if (!preset) return;
    hooks.panic();
    // A preset is a basic-oscillator patch, so it also resets the synth type
    // for a predictable sound (the row is hidden on the sampler engine anyway).
    // Switch the synth type first so the profile machinery loads that
    // instrument's sound, then the preset overrides it — otherwise the profile
    // load would clobber the patch the user just picked.
    store.update({ synthType: 'synth' });
    store.update({
      preset: id,
      waveform: preset.waveform,
      attack: preset.attack,
      release: preset.release,
      filter: preset.filter,
      echo: preset.echo,
      reverb: preset.reverb,
    });
    applyLoadedSoundToAudio();
    syncFromState();
    hooks.renderExtraParams();
    hooks.rebuildSource();
  }

  function updatePresetActive(): void {
    markStripActive(dom.presetButtons, store.get('preset'));
  }
  // Tweaking any sound control flips the preset to "custom" (markCustom); keep
  // the highlighted button in sync with that.
  store.on(['preset'], updatePresetActive);

  // ---- register curve preview ---------------------------------------------
  function paintCurve(): void {
    const s = store.getSettings();
    dom.bassCurveValue.textContent = s.bassCurveDb.toFixed(1);
    dom.trebleCurveValue.textContent = s.trebleCurveDb.toFixed(1);
    if (s.engine === 'sampler' && !s.registerCurveOnSamples) {
      dom.curvePreview.textContent = t('curve.notApplied');
      return;
    }
    const refs: Array<[string, number]> = [
      ['A0', 9],
      ['C2', 24],
      ['C4', 48],
      ['C6', 72],
      ['C8', 96],
    ];
    dom.curvePreview.textContent = refs
      .map(([name, abs]) => {
        const db = registerCurveDb(abs, s);
        return `${name} ${db >= 0 ? '+' : ''}${db.toFixed(1)} dB`;
      })
      .join('  ·  ');
  }

  // ---- per-instrument sound profiles --------------------------------------
  // The six sound knobs (plus a synth's waveform/preset) belong to the current
  // instrument, not the app: switching instrument saves the outgoing sound and
  // loads the incoming one. A sampled instrument seen for the first time starts
  // dry — no envelope, full level and brightness, no echo/reverb — because a
  // recording already carries its own character; a synth starts from the
  // generic oscillator defaults.
  // Real recorded instruments start dry — no synthetic envelope, full level and
  // brightness, no echo/reverb — but with the longest release, so notes ring out
  // naturally when you lift a key.
  const SAMPLER_SOUND_DEFAULTS: SoundProfile = {
    attack: 0,
    release: 10,
    volume: 1,
    filter: 12000,
    echo: 0,
    reverb: 0,
  };
  // Synth defaults are fixed here rather than read from DEFAULT_SETTINGS, whose
  // top-level sound now describes the piano (the boot instrument).
  const synthSoundDefaults = (): SoundProfile => ({
    attack: 0.01,
    release: 0.9,
    volume: 0.3,
    filter: 5200,
    echo: 0,
    reverb: 0.18,
    waveform: 'triangle',
    preset: 'piano',
  });
  const profileKey = (s: Settings): string =>
    s.engine === 'sampler' ? `sampler:${s.sampleInstrument}` : `synth:${s.synthType}`;

  function snapshotSound(s: Settings, forSynth: boolean): SoundProfile {
    const base: SoundProfile = {
      attack: s.attack,
      release: s.release,
      volume: s.volume,
      filter: s.filter,
      echo: s.echo,
      reverb: s.reverb,
    };
    return forSynth ? { ...base, waveform: s.waveform, preset: s.preset } : base;
  }

  // Programmatically loaded knob values never fire the dial 'change' handlers
  // that normally drive the graph, so the loaded sound is pushed in by hand.
  function applyLoadedSoundToAudio(): void {
    const s = store.getSettings();
    setVolume(s.volume);
    setFilterFrequency(s.filter);
    setEchoWet(s.echo);
    setReverbWet(s.reverb);
    hooks.applySound();
  }

  let currentProfileKey = profileKey(store.getSettings());
  store.on(['engine', 'synthType', 'sampleInstrument'], (s) => {
    const key = profileKey(s);
    if (key === currentProfileKey) return;
    // `s` still holds the outgoing instrument's knob values at this point.
    const outgoingIsSynth = currentProfileKey.startsWith('synth:');
    const savedProfiles = { ...store.get('profiles'), [currentProfileKey]: snapshotSound(s, outgoingIsSynth) };
    const incoming = savedProfiles[key] ?? (s.engine === 'sampler' ? SAMPLER_SOUND_DEFAULTS : synthSoundDefaults());
    currentProfileKey = key;
    const patch: Partial<Settings> = { profiles: savedProfiles, ...incoming };
    // Every instrument selection loads at the fullest available sample density.
    if (s.engine === 'sampler') patch.sampleDensity = 1;
    store.update(patch);
    applyLoadedSoundToAudio();
    syncFromState();
  });

  // Reset just the current instrument's sound to its defaults (dry for samples,
  // generic oscillator for synths), including a synth's extra parameters.
  function resetCurrentInstrument(): void {
    const s = store.getSettings();
    hooks.panic();
    if (s.engine === 'sampler') {
      store.update({ ...SAMPLER_SOUND_DEFAULTS, sampleDensity: 1 });
      applyLoadedSoundToAudio();
      syncFromState();
      hooks.rebuildSource();
      return;
    }
    const extraValues = { ...s.extraValues, [s.synthType]: defaultExtraValues()[s.synthType] };
    store.update({ ...synthSoundDefaults(), extraValues });
    applyLoadedSoundToAudio();
    syncFromState();
    hooks.renderExtraParams();
    hooks.rebuildSource();
  }
  dom.resetInstrumentBtn.addEventListener('click', resetCurrentInstrument);

  // ---- metronome -----------------------------------------------------------
  const BPM_MIN = 20;
  const BPM_MAX = 400;
  const activeAccents = (): AccentLevel[] => [...accentsFor(store.get('meterId'), store.get('meterCustom'))];

  const PULSE_COLOR: Record<AccentLevel, string> = { 2: 'var(--accent)', 1: '#9aa4bf', 0: '#6a7285' };
  const flashPulse = (level: AccentLevel): void => {
    dom.metronomePulse.style.background = PULSE_COLOR[level];
    window.setTimeout(() => {
      dom.metronomePulse.style.background = '';
    }, 90);
  };

  dom.metronomeBtn.textContent = t('metronome.start'); // initial label (HTML is empty)
  dom.metronomeBtn.addEventListener('click', () => {
    const running = metronome.toggle(store.get('bpm'), activeAccents(), flashPulse);
    dom.metronomeBtn.textContent = running ? t('metronome.stop') : t('metronome.start');
    dom.metronomeBtn.setAttribute('aria-pressed', String(running));
    if (!running) dom.metronomePulse.style.background = '';
  });

  // BPM steppers: ±1 per click, ±10 with Shift, with hold-to-repeat.
  function nudgeBpm(delta: number): void {
    const next = clamp(store.get('bpm') + delta, BPM_MIN, BPM_MAX);
    if (next === store.get('bpm')) return;
    store.update({ bpm: next });
    dom.bpm.value = String(next);
    metronome.setBpm(next);
  }
  function holdRepeat(btn: HTMLElement, step: () => void): void {
    let timer: number | null = null;
    let interval: number | null = null;
    const stop = (): void => {
      if (timer !== null) clearTimeout(timer);
      if (interval !== null) clearInterval(interval);
      timer = interval = null;
    };
    btn.addEventListener('mousedown', () => {
      step();
      timer = window.setTimeout(() => {
        interval = window.setInterval(step, 60);
      }, 350);
    });
    for (const evt of ['mouseup', 'mouseleave'] as const) btn.addEventListener(evt, stop);
  }
  holdRepeat(dom.bpmDown, () => nudgeBpm(-1));
  holdRepeat(dom.bpmUp, () => nudgeBpm(1));
  dom.bpmDown.addEventListener('click', (e) => {
    if (e.detail === 0) nudgeBpm(e.shiftKey ? -10 : -1); // keyboard activation
  });
  dom.bpmUp.addEventListener('click', (e) => {
    if (e.detail === 0) nudgeBpm(e.shiftKey ? 10 : 1);
  });

  // Tap tempo: average the recent inter-tap intervals into a BPM.
  let taps: number[] = [];
  dom.tapTempo.addEventListener('click', () => {
    const now = performance.now();
    // A long gap starts a fresh count rather than averaging across a pause.
    if (taps.length > 0 && now - taps[taps.length - 1]! > 2000) taps = [];
    taps.push(now);
    if (taps.length > 6) taps.shift();
    if (taps.length >= 2) {
      const span = taps[taps.length - 1]! - taps[0]!;
      const bpm = clamp(Math.round((60000 * (taps.length - 1)) / span), BPM_MIN, BPM_MAX);
      store.update({ bpm });
      dom.bpm.value = String(bpm);
      metronome.setBpm(bpm);
    }
  });

  // Meter selection + custom accent builder.
  binder.field({
    el: dom.meter,
    key: 'meterId',
    blurOnChange: true,
    onChange: () => {
      metronome.setAccents(activeAccents());
      syncMeterUi();
    },
  });

  function cycleAccent(level: AccentLevel): AccentLevel {
    return level === 0 ? 2 : level === 2 ? 1 : 0; // weak → strong → medium → weak
  }
  const ACCENT_GLYPH: Record<AccentLevel, string> = { 2: '●', 1: '◐', 0: '○' };

  function renderMeterPulses(): void {
    const custom = store.get('meterCustom');
    dom.meterPulses.replaceChildren();
    custom.forEach((level, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'pulse-dot';
      b.dataset['level'] = String(level);
      b.textContent = ACCENT_GLYPH[level];
      b.setAttribute('aria-label', `Puls ${i + 1}: ${['slab', 'mediu', 'accentuat'][level]}`);
      b.addEventListener('click', () => {
        const next = [...store.get('meterCustom')];
        next[i] = cycleAccent(next[i] ?? 0);
        store.update({ meterCustom: next as AccentLevel[] });
        metronome.setAccents(activeAccents());
        renderMeterPulses();
      });
      dom.meterPulses.appendChild(b);
    });
  }
  function changePulseCount(delta: number): void {
    const cur = store.get('meterCustom');
    const len = clamp(cur.length + delta, 1, 12);
    if (len === cur.length) return;
    const next = cur.slice(0, len);
    while (next.length < len) next.push(0);
    store.update({ meterCustom: next as AccentLevel[] });
    metronome.setAccents(activeAccents());
    renderMeterPulses();
  }
  dom.meterMinus.addEventListener('click', () => changePulseCount(-1));
  dom.meterPlus.addEventListener('click', () => changePulseCount(1));

  function syncMeterUi(): void {
    const custom = store.get('meterId') === CUSTOM_METER_ID;
    show(dom.meterCustom, custom);
    if (custom) renderMeterPulses();
  }

  // ---- reset ---------------------------------------------------------------
  button('resetAllBtn').addEventListener('click', () => {
    hooks.panic();
    metronome.stop();
    dom.metronomeBtn.textContent = t('metronome.start');
    dom.metronomeBtn.setAttribute('aria-pressed', 'false');
    clearPersisted();
    store.replaceAll(DEFAULT_SETTINGS());
    currentProfileKey = profileKey(store.getSettings());
    syncFromState();
    hooks.onReset();
  });

  // ---- sampler status ------------------------------------------------------
  let lastSamplerStatus: SamplerStatus = { kind: 'idle' };
  onSamplerStatus((st) => {
    lastSamplerStatus = st;
    renderSamplerStatus(st);
    // Reflect the density actually in use, so the selector visibly advances from
    // the coarse pass to the full one as the background upgrade lands.
    if (st.kind === 'ready' && store.get('engine') === 'sampler') {
      dom.sampleDensity.value = String(st.density);
    }
  });

  function renderSamplerStatus(st: SamplerStatus): void {
    if (store.get('engine') !== 'sampler' || st.kind === 'idle') {
      setStatus(dom.samplerStatus, '');
      return;
    }
    const name = instrumentLabel(st.instrument);
    switch (st.kind) {
      case 'loading':
        setStatus(dom.samplerStatus, t('status.loading', { instrument: name, loaded: st.loaded, total: st.total }), 'warn');
        break;
      case 'ready':
        if (st.upgrading) {
          setStatus(dom.samplerStatus, t('status.upgrading', { count: st.count }), 'warn');
        } else if (st.cached) {
          setStatus(dom.samplerStatus, t('status.cached'), 'ok');
        } else {
          const failed = st.failed ? t('status.readyFailed', { failed: st.failed }) : '';
          setStatus(
            dom.samplerStatus,
            t('status.ready', { count: st.count, low: st.low, high: st.high, failed }),
            st.failed ? 'warn' : 'ok',
          );
        }
        break;
      case 'error':
        setStatus(dom.samplerStatus, st.code === 'noSamples' ? t('status.errNoSamples') : t('status.errLoadFailed'), 'err');
        break;
    }
  }

  // ---- state → DOM ---------------------------------------------------------
  function syncFromState(): void {
    const s: Settings = store.getSettings();
    binder.sync();
    updatePresetActive();
    updateWaveformActive();
    slider.setValues(s.startAbs, s.endAbs);
    paintRangeLabels();
    syncStartSelects();
    paintCurve();
    syncMeterUi();
    refreshEngineVisibility();
  }

  syncFromState();
  return { syncFromState };
}

export function setEngineNotice(text: string, kind: 'err' | '' = ''): void {
  const node = document.getElementById('engineNotice');
  if (node) setStatus(node, text, kind);
}
